import { SupabaseClient } from "@supabase/supabase-js";
import dayjs from "dayjs";

import { VenueWithLocations } from "../../legacy/venue_pages";
import { VenuePagesRow } from "../../models/venue_pages";
import createNoAuthSupabaseClient, {
  createSupabaseAdminClient,
} from "../../supabase/supabase";
import { logException } from "@punchup/sentry";

/**
 * Lists every published, live venue page (id + name only), ordered by name.
 * Public read via the no-auth client — for surfaces where any live venue can be
 * selected (e.g. the developer-token scope picker). "Live" mirrors
 * getVenuePageData: is_live AND a published_at in the past.
 */
export async function getPublishedLiveVenuePages(): Promise<
  Pick<VenuePagesRow, "id" | "name">[]
> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("venue_pages")
    .select("id, name")
    .eq("is_live", true)
    .lte("published_at", dayjs().toISOString())
    .order("name", { ascending: true });

  if (error) {
    logException(error, {
      tags: {
        what: "Error listing published live venue pages",
        supabase: true,
      },
    });
    throw new Error(
      `Failed to list published live venue pages: ${error.message}`
    );
  }

  return data ?? [];
}

/**
 * Fetches a venue page's display name by id, for token-driven surfaces (the
 * email-unsubscribe page) where the caller was already authenticated by a
 * signed token. Admin client on purpose: an unsubscribe link must keep
 * working even after the venue page is unpublished or taken off live.
 */
export async function getVenuePageNameById(
  venuePageId: string
): Promise<Pick<VenuePagesRow, "id" | "name"> | null> {
  const supabase = createSupabaseAdminClient();
  const { data, error } = await supabase
    .from("venue_pages")
    .select("id, name")
    .eq("id", venuePageId)
    .maybeSingle();

  if (error) {
    logException(error, {
      tags: {
        what: "Error fetching venue page name by id",
        supabase: true,
      },
    });
    throw new Error(
      `Failed to fetch venue page ${venuePageId}: ${error.message}`
    );
  }

  return data;
}

export async function getVenueWithLocations(
  supabase: SupabaseClient,
  identifier: { id?: string; slug?: string }
): Promise<VenueWithLocations> {
  const { data } = await supabase
    .from("venue_pages")
    .select("*")
    .eq(identifier.id ? "id" : "slug", identifier.id ?? identifier.slug)
    .maybeSingle()
    .throwOnError();

  if (!data) {
    const idType = identifier.id ? "id" : "slug";
    const value = identifier.id ?? identifier.slug;
    throw new Error(`No venue with location found with ${idType}: ${value}`);
  }

  if (data.is_location_hidden || !data.location_ids?.length) {
    return { ...data, locations: [] };
  }

  // If location is not hidden, use Supabase Admin to fetch details of the locations
  // This ensures that we can return only the locations of venues that are not hidden
  const supabaseAdmin = createSupabaseAdminClient();

  const { data: locations } = await supabaseAdmin
    .from("venues")
    .select("*")
    .in("id", data.location_ids)
    .throwOnError();

  // Because we don't have strict foreign key constraints, we log an error in case some locations are not found.
  // We gracefully return locations that are found without throwing an exception.
  if (data.location_ids.length !== locations?.length) {
    logException(
      new Error(
        `Error: venue page named ${data.name} has a location_id that doesn't exist in the venues table. This could be due to a location being deleted and this venue page row not being updated. Venue page id: ${data.id} locations: ${data.location_ids.join(", ")}`
      )
    );
  }

  return { ...data, locations: locations ?? [] };
}
